import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import "../../../styles/ComponentStyles/Chatbot.css";
import { FiSend, FiX } from "react-icons/fi";
import { sendCombatChatMessage } from "../../../services/chatbot.service";

const CHAT_STORAGE_KEY = "combat_chat_messages";


const ChatbotModal = ({ onClose }) => {
  const panelRef = useRef(null);
  const messagesEndRef = useRef(null);
 const [messages, setMessages] = useState(() => {
    const storedMessages = sessionStorage.getItem(CHAT_STORAGE_KEY);
    if (storedMessages) {
      return JSON.parse(storedMessages);
    }
    return [
      {
        id: 1,
        text: "Hello! I'm your Combat AI Assistant. How can I help you today?",
        sender: "bot",
      },
    ];
  });
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  // Lock page scroll when modal opens
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    gsap.fromTo(
      panelRef.current,
      { x: 360, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.4, ease: "power3.out" }
    );

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const closePanel = () => {
    gsap.to(panelRef.current, {
      x: 360,
      opacity: 0,
      duration: 0.3,
      ease: "power3.in",
      onComplete: () => {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        document.body.style.paddingRight = '';
        onClose();
      },
    });
  };

  // -----------------------------
  // UPDATED: Send message to LB4
  // -----------------------------
  const handleSendMessage = async () => {
  if (!inputText.trim()) return;

  const userMessage = {
    id: Date.now(),
    text: inputText,
    sender: "user",
  };

  setMessages((prev) => [...prev, userMessage]);
  setInputText("");
  setIsTyping(true);

  try {
    const data = await sendCombatChatMessage(userMessage.text);

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + 1,
        text: data.answer,
        sender: "bot",
      },
    ]);
  } catch (error) {
    console.error("Chatbot error:", error);
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + 2,
        text: "⚠️ Server is not responding. Please try again.",
        sender: "bot",
      },
    ]);
  } finally {
    setIsTyping(false);
  }
};


  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickReplies = [
    "Book Strategy Call",
    "AI Solutions",
    "Pricing Info",
    "Contact Sales"
  ];

  // -----------------------------
  // UPDATED: quick replies call service
  // -----------------------------
const handleQuickReply = async (reply) => {
  const userMessage = {
    id: Date.now(),
    text: reply,
    sender: "user",
  };

  setMessages((prev) => [...prev, userMessage]);
  setIsTyping(true);

  try {
    const data = await sendCombatChatMessage(reply);

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + 1,
        text: data.answer,
        sender: "bot",
      },
    ]);
  } catch (error) {
    console.error("Chatbot error:", error);
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + 2,
        text: "⚠️ Server is not responding. Please try again.",
        sender: "bot",
      },
    ]);
  } finally {
    setIsTyping(false);
  }
};

  const handlePanelClick = (e) => {
    e.stopPropagation();
  };

  return (
    <>
      <div 
        className="chatbot-backdrop" 
        onClick={closePanel}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.17)',
          zIndex: 999,
        }}
      />
      
      <div className="chatbot-panel" ref={panelRef} onClick={handlePanelClick}>
        <div className="chatbot-header">
          <span>Combat AI Assistant</span>
          <button className="close-btn" onClick={closePanel}>
            <FiX />
          </button>
        </div>

        <div className="chatbot-body">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.sender === "user" ? "user-msg" : "bot-msg"}`}
            >
              {message.text}
            </div>
          ))}
          
          {isTyping && (
            <div className="typing-indicator">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          )}
          
          {!isTyping && messages.length <= 2 && (
            <div className="quick-replies">
              <div className="quick-reply-buttons">
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    className="quick-reply-btn"
                    onClick={() => handleQuickReply(reply)}
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <div className="chatbot-input">
          <input 
            placeholder="Type your message..." 
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isTyping}
          />
          <button 
            className="send-btn"
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isTyping}
          >
            <FiSend />
          </button>
        </div>
      </div>
    </>
  );
};

export default ChatbotModal;
